import type { StatId } from '../domain/stats.ts';
import type { Roller } from './dice.ts';
import type { RollMode } from './rollModes.ts';
import type { CheckResult } from './checks.ts';
import { resolveSkillCheck } from './checks.ts';
import type { Resolution } from './degrees.ts';
import { resolveAgainstDifficulty } from './degrees.ts';

/**
 * ATTACK RESOLUTION - does the attack land?
 *
 * Deliberately separate from damage (see rules/damageRoll.ts). An attack is
 * resolved, and only then is damage rolled. Keeping the two apart is what will
 * let a future GM module supply the target automatically without touching the
 * damage code.
 *
 * RULE DCC-ATK-001 - C&C p.22; Rules for Survival p.34.
 * An Attack Skill Check is made against the target's Evade value, which sets
 * the Difficulty of the attack.
 *
 * RULE DCC-ATK-002 - C&C p.22.
 * A Mob's Evade is 10 + Dex Mod + Floor Number. The player application does NOT
 * compute this: no Mob is modelled, so the GM supplies the number. The formula
 * is offered only as an optional helper.
 */
export interface AttackCheckInput {
  skillId: string;
  skillName: string;
  /** Stat added to hit. From the attack profile, but overridable by the player. */
  toHitStat: StatId;
  toHitStatAbbr: string;
  toHitStatMod: number;
  rank: number;
  mode: RollMode;
  extraComponents?: readonly { label: string; value: number }[];
}

export interface AttackCheckOutcome {
  check: CheckResult;
  /** Null when the GM has not revealed the target's Evade. */
  resolution: Resolution | null;
}

export function resolveAttackCheck(
  roller: Roller,
  input: AttackCheckInput,
  targetEvade: number | null,
): AttackCheckOutcome {
  const check = resolveSkillCheck(roller, {
    skillId: input.skillId,
    skillName: input.skillName,
    stat: input.toHitStat,
    statAbbr: input.toHitStatAbbr,
    rank: input.rank,
    statMod: input.toHitStatMod,
    mode: input.mode,
    extraComponents: input.extraComponents,
  });

  return {
    check,
    resolution: targetEvade === null ? null : resolveAgainstDifficulty(check, targetEvade),
  };
}

/**
 * Optional helper for a GM who wants the standard Mob Evade.
 * Never used automatically; the player screen asks for a number.
 */
export function mobEvadeValue(targetDexMod: number, floor: number): number {
  if (!Number.isInteger(floor) || floor < 1) {
    throw new Error(`Floor Number must be an integer of at least 1, received ${floor}`);
  }
  return 10 + targetDexMod + floor;
}

/** Whether a resolved attack hit at all. */
export function attackHit(resolution: Resolution | null): boolean | null {
  return resolution === null ? null : resolution.success;
}
